import {useEffect, useState} from "react";
import {useSite} from "@/lib/site";
import {Clock, Star, BookOpen, GraduationCap, Music, Bell, BellRing, Sun, Moon, Loader2} from "lucide-react";
import {apiGet, apiPut, apiPost} from "@/lib/api";
import {enablePush} from "@/components/NotificationBell";

const CITIES = {
  "Ahmedabad": [23.0225, 72.5714, 5.5], "Mumbai": [19.076, 72.877, 5.5], "Surat": [21.170, 72.831, 5.5],
  "London": [51.507, -0.127, 0.0], "Leicester": [52.636, -1.139, 0.0], "New York": [40.713, -74.006, -5.0],
  "New Jersey": [40.058, -74.406, -5.0], "Toronto": [43.651, -79.347, -5.0], "Dubai": [25.204, 55.270, 4.0],
  "Nairobi": [-1.292, 36.822, 3.0], "Sydney": [-33.868, 151.209, 10.0],
};
const SOON = {
  books: [BookOpen, "Books & Scriptures", "Bhagavad Gita, Shikshapatri, Narsinh Mehta's padas and Gujarati classics to read in English and Gujarati."],
  learn: [GraduationCap, "Learn", "Handpicked YouTube lessons — Gujarati language, garba steps, recipes, rangoli and rituals."],
  bhajans: [Music, "Bhajans", "Bhajans, aartis and prabhatiyas to listen to, with lyrics and meanings."],
};
const TABS = [["choghadiya", "Choghadiya", Clock], ["rashifal", "Rashifal", Star], ["books", "Books & Scriptures", BookOpen], ["learn", "Learn", GraduationCap], ["bhajans", "Bhajans", Music]];
const mins = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };

function Slot({s, now}) {
  return (
    <div className={`cg-slot ${s.nature}${now ? " now" : ""}`} data-testid={`cg-slot-${s.name.toLowerCase()}`}>
      <strong>{s.name}</strong><span>{s.start} – {s.end}</span><small>{s.meaning}</small>
      {now && <b className="cg-now">NOW</b>}
    </div>
  );
}

function Choghadiya() {
  const [city, setCity] = useState("Ahmedabad");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [d, setD] = useState(null);
  useEffect(() => {
    const [lat, lon, tz] = CITIES[city];
    setD(null); apiGet("/choghadiya", {date, lat, lon, tz}).then(setD).catch(() => setD(false));
  }, [city, date]);
  const tz = CITIES[city][2];
  const local = new Date(Date.now() + tz * 36e5);
  const today = date === local.toISOString().slice(0, 10);
  const nowM = local.getUTCHours() * 60 + local.getUTCMinutes();
  const isNow = (s) => today && (mins(s.start) <= mins(s.end) ? nowM >= mins(s.start) && nowM < mins(s.end) : nowM >= mins(s.start) || nowM < mins(s.end));
  return (
    <div data-testid="culture-panel-choghadiya">
      <div className="panchang-controls">
        <label>City<select data-testid="cg-city" value={city} onChange={e => setCity(e.target.value)}>{Object.keys(CITIES).map(c => <option key={c}>{c}</option>)}</select></label>
        <label>Date<input data-testid="cg-date" type="date" value={date} onChange={e => setDate(e.target.value)}/></label>
      </div>
      {d === null ? <div className="empty-pane"><Loader2 className="spin"/></div> : d === false ? <div className="empty-pane"><p>Could not compute choghadiya.</p></div> : (
        <>
          <p className="cg-meta" data-testid="cg-meta">{d.weekday} · Sunrise {d.sunrise} · Sunset {d.sunset}</p>
          <div className="cg-cols">
            <section><div className="eyebrow"><Sun size={11}/> DAY CHOGHADIYA</div><div className="cg-list" data-testid="cg-day">{d.day.map((s, i) => <Slot key={i} s={s} now={isNow(s)}/>)}</div></section>
            <section><div className="eyebrow"><Moon size={11}/> NIGHT CHOGHADIYA</div><div className="cg-list" data-testid="cg-night">{d.night.map((s, i) => <Slot key={i} s={s} now={isNow(s)}/>)}</div></section>
          </div>
          <p className="cg-note">{d.method} Amrit, Shubh and Labh are auspicious; Chal is fine for travel; avoid Udveg, Kaal and Rog for new beginnings.</p>
        </>
      )}
    </div>
  );
}

function MorningAlert({rashis, pick, notify}) {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { apiGet("/users/me/rashifal-alert").then(d => setA({rashi: d.rashi || pick || rashis[0], city: d.city || "Ahmedabad", on: !!d.on})).catch(() => setA({rashi: pick || rashis[0], city: "Ahmedabad", on: false})); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!a) return null;
  const save = async (next) => {
    setBusy(true);
    try {
      if (next.on && !a.on && typeof Notification !== "undefined" && Notification.permission !== "granted") await enablePush(notify);
      const [lat, lon, tz] = CITIES[next.city];
      await apiPut("/users/me/rashifal-alert", {...next, lat, lon, tz});
      setA(next);
      if (next.on !== a.on) notify(next.on ? `Morning alert on — 7am ${next.city} time` : "Morning alert off");
    } catch { notify("Could not save your alert"); }
    finally { setBusy(false); }
  };
  const test = async () => { try { await apiPost("/users/me/rashifal-alert/test", {}); notify("Sent — check your bell and phone"); } catch (e) { notify(e?.response?.data?.detail || "Could not send"); } };
  return (
    <div className={a.on ? "morning-alert on" : "morning-alert"} data-testid="rashifal-alert-card">
      <span className="ma-icon"><BellRing size={18}/></span>
      <div className="ma-text"><strong>Morning rashifal alert</strong><small>Your daily rashifal, the best choghadiya and any festival today (Ekadashi, Navratri and more), on your phone at 7am in your city.</small></div>
      <select data-testid="ra-rashi" value={a.rashi} disabled={busy} onChange={e => save({...a, rashi: e.target.value})}>{rashis.map(r => <option key={r}>{r}</option>)}</select>
      <select data-testid="ra-city" value={a.city} disabled={busy} onChange={e => save({...a, city: e.target.value})}>{Object.keys(CITIES).map(c => <option key={c}>{c}</option>)}</select>
      <button data-testid="ra-toggle" className={a.on ? "outline-button sm" : "coral-button sm"} disabled={busy} onClick={() => save({...a, on: !a.on})}>{a.on ? "Turn off" : "Turn on"}</button>
      {a.on && <button data-testid="ra-test" className="text-button" onClick={test}>Send me one now</button>}
    </div>
  );
}

function Rashifal({notify}) {
  const [period, setPeriod] = useState("daily");
  const [d, setD] = useState(null);
  const [pick, setPick] = useState(() => localStorage.getItem("io-rashi") || "");
  useEffect(() => { setD(null); apiGet("/rashifal", {period}).then(setD).catch(() => setD(false)); }, [period]);
  const choose = (r) => { setPick(r); localStorage.setItem("io-rashi", r); };
  const list = d?.rashis ? [...d.rashis].sort((a, b) => (b.rashi === pick) - (a.rashi === pick)) : [];
  const key = (r) => r.rashi.split(" ")[0].toLowerCase();
  return (
    <div data-testid="culture-panel-rashifal">
      <div className="activity-tabs sub">
        {["daily", "weekly", "monthly"].map(p => <button key={p} data-testid={`rashifal-${p}`} className={period === p ? "activity-tab active" : "activity-tab"} onClick={() => setPeriod(p)}>{p[0].toUpperCase() + p.slice(1)}</button>)}
      </div>
      {d === null ? <div className="empty-pane"><Loader2 className="spin"/></div> : d === false ? <div className="empty-pane"><p>Could not load rashifal.</p></div> : (
        <>
          <MorningAlert rashis={d.rashis.map(r => r.rashi)} pick={pick} notify={notify}/>
          <p className="cg-meta" data-testid="rashifal-sky">Moon in {d.moon_sign} · Sun in {d.sun_sign} · {period === "daily" ? "Today" : period === "weekly" ? `Week of ${d.date}` : new Date(d.date).toLocaleDateString("en-GB", {month: "long", year: "numeric"})}</p>
          <div className="rashi-grid">
            {list.map(r => (
              <article key={r.rashi} className={r.rashi === pick ? "rashi-card mine" : "rashi-card"} data-testid={`rashi-${key(r)}`}>
                <div className="rashi-head"><h3>{r.rashi}</h3><button data-testid={`rashi-pick-${key(r)}`} onClick={() => choose(r.rashi === pick ? "" : r.rashi)}>{r.rashi === pick ? "My rashi ✓" : "Set as mine"}</button></div>
                <p>{r.text}</p>
                <small>Lucky {period === "daily" ? "choghadiya" : period === "weekly" ? "day" : "colour"}: <b>{r.lucky}</b></small>
              </article>
            ))}
          </div>
          <p className="cg-note">{d.method} For guidance only.</p>
        </>
      )}
    </div>
  );
}

function Soon({id, notify}) {
  const [Icon, title, text] = SOON[id];
  return (
    <div className="culture-soon" data-testid={`culture-panel-${id}`}>
      <span className="culture-icon"><Icon size={26}/></span>
      <span className="pill">COMING SOON</span>
      <h2>{title}</h2>
      <p>{text}</p>
      <button data-testid="culture-notify" className="coral-button" onClick={() => notify(`We'll let you know when ${title} goes live`)}>Notify me <Bell size={15}/></button>
    </div>
  );
}

export default function Culture({notify}) {
  const t = useSite();
  const [tab, setTab] = useState("choghadiya");
  return (
    <div data-testid="culture-page">
      <div className="page-title">
        <div>
          <div className="eyebrow">CULTURE · SANSKRUTI</div>
          <h1>Roots that <em>travel with you.</em></h1>
          <p>{t("culture_sub")}</p>
        </div>
      </div>
      <div className="activity-tabs" data-testid="culture-tabs">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} data-testid={`culture-tab-${id}`} className={tab === id ? "activity-tab active" : "activity-tab"} onClick={() => setTab(id)}>
            <Icon size={13}/> {label}{SOON[id] && <span className="soon-dot">SOON</span>}
          </button>
        ))}
      </div>
      {tab === "choghadiya" && <Choghadiya/>}
      {tab === "rashifal" && <Rashifal notify={notify}/>}
      {SOON[tab] && <Soon id={tab} notify={notify}/>}
    </div>
  );
}
